import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { KpisMachinesWithDates } from "interfaces/kpis";
import { ENABLE_REDUX_DEVTOOLS } from "stores/constants";

type Order = "asc" | "desc";

interface SortParams {
  by: string;
  order: Order;
}

export interface State {
  available: KpisMachinesWithDates[];
  selected: KpisMachinesWithDates[];
  availableChecked: KpisMachinesWithDates[];
  isAvailableChecked: (item: KpisMachinesWithDates) => boolean;
  isAvailableAllChecked: () => boolean;
  isAvailableIndeterminate: () => boolean;
  selectedChecked: KpisMachinesWithDates[];
  isSelectedChecked: (item: KpisMachinesWithDates) => boolean;
  isSelectedAllChecked: () => boolean;
  isSelectedIndeterminate: () => boolean;
  filterValue: Partial<KpisMachinesWithDates> | null;
  filteredAvailable: KpisMachinesWithDates[];
  filteredSelected: KpisMachinesWithDates[];
  sortParams: SortParams;
  init: (
    available: KpisMachinesWithDates[],
    selected: KpisMachinesWithDates[]
  ) => void;
  setFilterValue: (filterValue: Partial<KpisMachinesWithDates> | null) => void;
  setAvailableChecked: (items: KpisMachinesWithDates[]) => void;
  removeAvailableChecked: (items: KpisMachinesWithDates[]) => void;
  setSelectedChecked: (items: KpisMachinesWithDates[]) => void;
  removeSelectedChecked: (items: KpisMachinesWithDates[]) => void;
  addAvailable: (items: KpisMachinesWithDates[]) => void;
  addSelected: (items: KpisMachinesWithDates[]) => void;
  updateFilteredLists: <T extends Partial<KpisMachinesWithDates>>(
    filters: T
  ) => void;
  setSortParams: (columnName: SortParams["by"]) => void;
  reset: () => void;
  clearChecked: () => void;
}

// functions to sort a list of Machine[] by name then id
const sortMachinesByName = (
  a: KpisMachinesWithDates,
  b: KpisMachinesWithDates
): number => {
  if (a.name < b.name) {
    return -1;
  }
  if (a.name > b.name) {
    return 1;
  }
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
};

// function to sort a list of Machine[] by an argument and order by another argument
export const sortMachines: (
  a: any,
  b: any,
  sortParams: SortParams
) => number = (rowA, rowB, sortParams) => {
  if (rowA[sortParams.by] < rowB[sortParams.by]) {
    return sortParams.order === "asc" ? -1 : 1;
  }

  if (rowA[sortParams.by] > rowB[sortParams.by]) {
    return sortParams.order === "asc" ? 1 : -1;
  }

  return 0;
};

export const useListBuilderStore = create<State>()(
  devtools(
    (set, get) => ({
      available: [],
      selected: [],
      availableChecked: [],
      selectedChecked: [],
      filterValue: null,
      filteredAvailable: [],
      filteredSelected: [],
      sortParams: { by: "name", order: "asc" },
      init: (
        available: KpisMachinesWithDates[],
        selected: KpisMachinesWithDates[]
      ) => {
        set(
          { available, selected, filterValue: null },
          false,
          "LIST_BUILDER_STORE/init"
        );
      },
      setAvailableChecked: (items: KpisMachinesWithDates[]) => {
        const filteredItems = items.filter(
          item => !get().availableChecked.includes(item)
        );

        set(
          state => ({
            availableChecked: [...state.availableChecked, ...filteredItems]
          }),
          false,
          "LIST_BUILDER_STORE/setAvailableChecked"
        );
      },
      isAvailableChecked: (item: KpisMachinesWithDates) => {
        return get().availableChecked.includes(item);
      },
      isAvailableAllChecked: () => {
        return get().filteredAvailable.every(item =>
          get().availableChecked.includes(item)
        );
      },
      isAvailableIndeterminate: () => {
        return (
          get().availableChecked.length > 0 &&
          !get().isAvailableAllChecked() &&
          get().filteredAvailable.some(item =>
            get().availableChecked.includes(item)
          )
        );
      },
      setFilterValue: (filterValue: Partial<KpisMachinesWithDates> | null) => {
        set(
          state => ({
            filterValue:
              !filterValue || !Object.keys(filterValue).length
                ? null
                : { ...state.filterValue, ...filterValue }
          }),
          false,
          "LIST_BUILDER_STORE/setFilterValue"
        );
      },
      removeAvailableChecked: (items: KpisMachinesWithDates[]) => {
        set(
          state => ({
            availableChecked: state.availableChecked.filter(
              item => !items.includes(item)
            )
          }),
          false,
          "LIST_BUILDER_STORE/removeAvailableChecked"
        );
      },
      setSelectedChecked: (items: KpisMachinesWithDates[]) => {
        const filteredItems = items.filter(
          item => !get().selectedChecked.includes(item)
        );

        set(
          state => ({
            selectedChecked: [...state.selectedChecked, ...filteredItems]
          }),
          false,
          "LIST_BUILDER_STORE/setSelectedChecked"
        );
      },
      isSelectedChecked: (item: KpisMachinesWithDates) => {
        return get().selectedChecked.includes(item);
      },
      isSelectedAllChecked: () => {
        return get().filteredSelected.every(item =>
          get().selectedChecked.includes(item)
        );
      },
      isSelectedIndeterminate: () => {
        return (
          get().selectedChecked.length > 0 &&
          !get().isSelectedAllChecked() &&
          get().filteredSelected.some(item =>
            get().selectedChecked.includes(item)
          )
        );
      },
      removeSelectedChecked: (items: KpisMachinesWithDates[]) => {
        set(
          state => ({
            selectedChecked: state.selectedChecked.filter(
              item => !items.includes(item)
            )
          }),
          false,
          "LIST_BUILDER_STORE/removeSelectedChecked"
        );
      },
      addAvailable: (items: KpisMachinesWithDates[]) => {
        set(
          state => ({
            available: [
              ...state.available,
              ...state.selected.filter(item => items.includes(item))
            ].sort(sortMachinesByName),
            selected: state.selected.filter(item => !items.includes(item)),
            selectedChecked: []
          }),
          false,
          "LIST_BUILDER_STORE/addAvailable"
        );
      },
      addSelected: (items: KpisMachinesWithDates[]) => {
        set(
          state => ({
            selected: [
              ...state.selected,
              ...state.available.filter(item => items.includes(item))
            ].sort(sortMachinesByName),
            available: state.available.filter(item => !items.includes(item)),
            availableChecked: []
          }),
          false,
          "LIST_BUILDER_STORE/addSelected"
        );
      },
      updateFilteredLists: <T extends Partial<KpisMachinesWithDates>>(
        filters: T
      ) => {
        const { available, selected } = get();

        const filterFn = (item: KpisMachinesWithDates): boolean =>
          Object.entries(filters).every(([key, value]) =>
            value === undefined || value === null || value === false
              ? true // Do not filter on undefined, null, or false
              : item[key as keyof KpisMachinesWithDates] === value
          );

        set(
          {
            filteredAvailable: Object.keys(filters).length
              ? available.filter(filterFn)
              : available,
            filteredSelected: Object.keys(filters).length
              ? selected.filter(filterFn)
              : selected
          },
          false,
          "LIST_BUILDER_STORE/updateFilteredLists"
        );
      },
      setSortParams: columnName => {
        set(
          prev => {
            let order: Order = "asc";
            if (prev.sortParams.by === columnName) {
              order = prev.sortParams.order === "asc" ? "desc" : "asc";
            }

            return {
              sortParams: {
                by: columnName,
                order
              }
            };
          },
          false,
          "LIST_BUILDER_STORE/setSortParams"
        );
      },
      reset: () => {
        set(
          {
            available: [],
            selected: [],
            availableChecked: [],
            selectedChecked: [],
            filterValue: null,
            filteredAvailable: [],
            filteredSelected: [],
            sortParams: { by: "name", order: "asc" }
          },
          false,
          "LIST_BUILDER_STORE/reset"
        );
      },
      clearChecked: () => {
        set(
          {
            availableChecked: [],
            selectedChecked: []
          },
          false,
          "LIST_BUILDER_STORE/clearChecked"
        );
      }
    }),
    {
      enabled: ENABLE_REDUX_DEVTOOLS,
      name: "ListBuilderStore"
    }
  )
);
